import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Check, User, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WaitlistForm = ({ id = "waitlist", variant = "default" }) => {
    const [userType, setUserType] = useState("individual"); // "individual" | "community"
    const [form, setForm] = useState({
        name: "",
        email: "",
        community_name: "",
    });
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [position, setPosition] = useState(null);
    const [error, setError] = useState("");

    const isHero = variant === "hero";
    const isCommunity = userType === "community";

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name.trim() || !form.email.trim()) {
            setError("Please enter your name and email.");
            return;
        }
        if (isCommunity && !form.community_name.trim()) {
            setError("Please enter your community name.");
            return;
        }
        setStatus("loading");
        try {
            const res = await axios.post(`${API}/waitlist`, {
                name: form.name.trim(),
                email: form.email.trim(),
                source: isHero ? "hero" : "waitlist_section",
                user_type: userType,
                community_name: isCommunity ? form.community_name.trim() : null,
            });
            setPosition(res.data.position);
            setStatus("success");
        } catch (err) {
            const detail =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            setError(typeof detail === "string" ? detail : "Please enter a valid email.");
            setStatus("error");
        }
    };

    if (status === "success") {
        return (
            <motion.div
                id={id}
                data-testid="waitlist-success"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-violet-400/30 bg-gradient-to-br from-violet-500/15 to-fuchsia-500/10 p-6 md:p-8 text-center"
            >
                <div className="mx-auto h-12 w-12 rounded-full bg-violet-500/20 grid place-items-center mb-3 ring-1 ring-violet-300/40">
                    <Check className="text-violet-200" />
                </div>
                <div className="font-display text-2xl font-extrabold text-white">
                    {isCommunity
                        ? "Your community is on the list."
                        : "You're a Founding Member."}
                </div>
                <p className="mt-2 text-white/70 text-sm md:text-base">
                    {position
                        ? `You're #${position} on the waitlist. We'll be in touch as Voro launches.`
                        : "We'll be in touch soon."}
                </p>
            </motion.div>
        );
    }

    // Hero variant — single-line individual-only form (keeps hero compact)
    if (isHero) {
        return (
            <form
                id={id}
                onSubmit={submit}
                data-testid="waitlist-form"
                className="w-full flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
            >
                <input
                    data-testid="waitlist-hero-name-input"
                    type="text"
                    placeholder="First name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full sm:w-40 rounded-full px-5 py-4 bg-white/[0.06] border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-violet-400/50 transition"
                />
                <input
                    data-testid="waitlist-hero-email-input"
                    type="email"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="flex-1 w-full rounded-full px-5 py-4 bg-white/[0.04] border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-violet-400/50 focus:bg-white/[0.06] transition"
                />
                <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="waitlist-hero-submit"
                    className="rounded-full px-7 py-4 bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-all hover:scale-[1.02] active:scale-100 shadow-[0_0_40px_rgba(139,92,246,0.45)] disabled:opacity-60 disabled:hover:scale-100 inline-flex items-center justify-center gap-2 whitespace-nowrap"
                >
                    {status === "loading" ? (
                        <>
                            <Loader2 className="animate-spin" size={18} />
                            Joining…
                        </>
                    ) : (
                        "Join Waitlist"
                    )}
                </button>
                {error && (
                    <div
                        data-testid="waitlist-error"
                        className="w-full text-sm text-rose-300/90 px-3"
                    >
                        {error}
                    </div>
                )}
            </form>
        );
    }

    // Default (waitlist section) variant — with user-type toggle
    return (
        <div className="w-full max-w-xl mx-auto">
            {/* User-type toggle */}
            <div
                role="tablist"
                aria-label="I'm joining as"
                data-testid="waitlist-type-toggle"
                className="relative grid grid-cols-2 gap-1.5 p-1.5 rounded-full bg-white/[0.04] border border-white/10 mb-5"
            >
                <button
                    type="button"
                    role="tab"
                    aria-selected={!isCommunity}
                    data-testid="waitlist-type-individual"
                    onClick={() => setUserType("individual")}
                    className={`relative z-10 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all ${
                        !isCommunity
                            ? "bg-violet-600 text-white shadow-[0_0_24px_rgba(139,92,246,0.45)]"
                            : "text-white/65 hover:text-white"
                    }`}
                >
                    <User size={14} />
                    I&apos;m Joining As An Individual
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={isCommunity}
                    data-testid="waitlist-type-community"
                    onClick={() => setUserType("community")}
                    className={`relative z-10 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all ${
                        isCommunity
                            ? "bg-violet-600 text-white shadow-[0_0_24px_rgba(139,92,246,0.45)]"
                            : "text-white/65 hover:text-white"
                    }`}
                >
                    <Users size={14} />
                    I&apos;m Registering A Community
                </button>
            </div>

            <form
                id={id}
                onSubmit={submit}
                data-testid="waitlist-form"
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
                <input
                    data-testid="waitlist-name-input"
                    type="text"
                    placeholder={isCommunity ? "Your name" : "Your name"}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full rounded-full px-5 py-4 bg-white/[0.04] border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-violet-400/50 focus:bg-white/[0.06] transition"
                />
                <input
                    data-testid="waitlist-email-input"
                    type="email"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full rounded-full px-5 py-4 bg-white/[0.04] border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-violet-400/50 focus:bg-white/[0.06] transition"
                />

                {isCommunity && (
                    <motion.input
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                        data-testid="waitlist-community-name-input"
                        type="text"
                        placeholder="Community name (e.g. Iron Peak Gym, Perth Run Club)"
                        value={form.community_name}
                        onChange={(e) =>
                            setForm({ ...form, community_name: e.target.value })
                        }
                        className="sm:col-span-2 w-full rounded-full px-5 py-4 bg-white/[0.04] border border-violet-400/30 text-white placeholder:text-white/40 focus:outline-none focus:border-violet-400/60 focus:bg-white/[0.06] transition"
                    />
                )}

                <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="waitlist-submit"
                    className="sm:col-span-2 rounded-full px-7 py-4 bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-all hover:scale-[1.02] active:scale-100 shadow-[0_0_40px_rgba(139,92,246,0.45)] disabled:opacity-60 disabled:hover:scale-100 inline-flex items-center justify-center gap-2 whitespace-nowrap"
                >
                    {status === "loading" ? (
                        <>
                            <Loader2 className="animate-spin" size={18} />
                            Joining…
                        </>
                    ) : isCommunity ? (
                        "Register Community"
                    ) : (
                        "Join Waitlist"
                    )}
                </button>

                {error && (
                    <div
                        data-testid="waitlist-error"
                        className="sm:col-span-2 text-sm text-rose-300/90 px-3"
                    >
                        {error}
                    </div>
                )}
            </form>
        </div>
    );
};

export default WaitlistForm;
